import {
    HOST_API,
    HOST_API_CRUISES,
    HOST_API_FLIGHTS,
} from "src/config-global";
import axios, { AxiosError, CanceledError } from "axios";
import qs from "qs";

import { queryClient } from "src/tanstack/QueryClient";
import { storageKeys } from "src/utils";
import { enqueueSnackbar } from "notistack";

//timeout up to 3 minutes for hotelbeds and TBO requirements
const axiosInstance = axios.create({
    baseURL: HOST_API,
    timeout: 280_000,
    withCredentials: true,
    headers: {
        "X-Client-TimeZone": Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
});

axiosInstance.interceptors.request.use(
    (config) => config,
    (error) => Promise.reject(new Error(error)),
);

const shouldRetryTimeoutRequest = (error) => {
    const { config, code, message } = error || {};

    if (!config || typeof config.retry !== "number" || config.retry <= 0) {
        return false;
    }

    const isTimeoutCode = code === "ECONNABORTED";
    const isTimeoutMessage =
        typeof message === "string" &&
        message.toLowerCase().includes("timeout");

    return isTimeoutCode && isTimeoutMessage;
};

const isTimeoutRequestError = (error) => {
    const { code, message } = error || {};
    const isTimeoutCode = code === "ECONNABORTED";
    const isTimeoutMessage =
        typeof message === "string" &&
        message.toLowerCase().includes("timeout");

    return isTimeoutCode || isTimeoutMessage;
};

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (error instanceof CanceledError) {
            return Promise.reject(error);
        }

        if (error instanceof AxiosError) {
            if (shouldRetryTimeoutRequest(error)) {
                const retryConfig = error.config;
                retryConfig.__retryCount = retryConfig.__retryCount || 0;

                if (retryConfig.__retryCount < retryConfig.retry) {
                    retryConfig.__retryCount += 1;
                    return axiosInstance(retryConfig);
                }
            }

            if (isTimeoutRequestError(error)) {
                const shouldExposeTimeoutError = Boolean(
                    error?.config?.handleTimeoutError,
                );

                if (shouldExposeTimeoutError) {
                    return Promise.reject({
                        code: "REQUEST_TIMEOUT",
                        message: error?.message || "Request timeout",
                        isTimeout: true,
                    });
                }
            }

            if (error?.response?.status === 498) {
                if (error.response?.data?.error === "missing_authentication") {
                    return Promise.reject("No Session");
                }
                if (error.response?.data?.error === "unable_to_refresh_token") {
                    return Promise.reject("Unable to refresh token");
                }
                appLogout(error.response.data.message || "Session Expired");
                return Promise.resolve("Session Expired");
            }

            return Promise.reject(
                error.response?.data || "Something went wrong",
            );
        }
        return Promise.reject("Unknown Error");
    },
);

const flightAPI = axios.create({
    baseURL: HOST_API_FLIGHTS,
});

flightAPI.interceptors.response.use(
    (res) => res,
    (err) =>
        Promise.reject(
            (err.response && err.response.data) || "Something went wrong",
        ),
);

const cruiseAPI = axios.create({
    baseURL: HOST_API_CRUISES,
    paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "brackets" }),
});

cruiseAPI.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem(storageKeys.TOKEN);
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error),
);

cruiseAPI.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error instanceof CanceledError) {
            return Promise.reject(error);
        }
        return Promise.reject(
            (error.response && error.response.data) || "Something went wrong",
        );
    },
);

function appLogout(message) {
    enqueueSnackbar(message, { variant: "error" });
    const queries = queryClient.getQueriesData();
    if (queries.length > 0) {
        queryClient.removeQueries({
            predicate: () => true,
        });
    }
    localStorage.removeItem(storageKeys.TOKEN);
    localStorage.removeItem(storageKeys.SUPERADMIN_TOKEN);
    sessionStorage.clear();
    delete axiosInstance.defaults.headers.common.Authorization;
    window.location.replace("/");
}

export { axiosInstance, flightAPI, cruiseAPI };
export default axiosInstance;
